import { useState, useEffect } from "react"
import { ChevronDown, ChevronUp, HelpCircle, Search, MessageCircle, Phone, Mail, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent } from "@/components/ui/card"
import { Link } from "react-router-dom"
import Seo from "@/seo/seo"
import { routeSeoConfig } from "@/utils/seo-config"
import axios from "@/utils/axios"

export default function FAQ() {
  const [searchTerm, setSearchTerm] = useState("")
  const [openItems, setOpenItems] = useState(new Set([0])) // First item open by default
  const [faqData, setFaqData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const res = await axios.get("/faq")
        setFaqData(res.data)
      } catch (error) {
        console.error("Error fetching FAQs:", error)
        setFaqData(null)
      } finally {
        setLoading(false)
      }
    }
    fetchFAQs()
  }, [])

  const defaultFaqCategories = [
    {
      title: "Product Information",
      faqs: [
        {
          question: "What ingredients do you use in your skincare products?",
          answer:
            "We use only the finest natural and organic ingredients sourced from sustainable farms worldwide. Our products contain botanical extracts, essential oils, vitamins, and scientifically-proven active ingredients like hyaluronic acid, niacinamide, and peptides. All our formulations are free from parabens, sulfates, and harmful chemicals.",
        },
        {
          question: "Are your products suitable for sensitive skin?",
          answer:
            "Yes! Our products are dermatologist-tested and formulated to be gentle on all skin types, including sensitive skin. We conduct extensive patch testing and use hypoallergenic ingredients. However, we always recommend doing a patch test before using any new product.",
        },
        {
          question: "Do you test on animals?",
          answer:
            "Absolutely not. We are 100% cruelty-free and never test our products on animals. We are certified by Leaping Bunny and committed to ethical beauty practices. We use alternative testing methods to ensure product safety and efficacy.",
        },
        {
          question: "How long do your products last once opened?",
          answer:
            "Most of our products have a shelf life of 12-24 months once opened, indicated by the PAO (Period After Opening) symbol on the packaging. Unopened products can last 2-3 years when stored properly in a cool, dry place away from direct sunlight.",
        },
      ],
    },
    {
      title: "Orders & Shipping",
      faqs: [
        {
          question: "How long does shipping take?",
          answer:
            "We offer multiple shipping options: Standard shipping (3-5 business days), Express shipping (1-2 business days), and Same-day delivery in Lagos. International shipping typically takes 7-14 business days depending on the destination.",
        },
        {
          question: "Do you offer free shipping?",
          answer:
            "Yes! We offer free standard shipping on orders over ₦15,000 within Nigeria. For international orders, free shipping is available on orders over ₦50,000. Express and same-day delivery options have additional charges.",
        },
        {
          question: "Can I track my order?",
          answer:
            "Once your order ships, you'll receive a tracking number via email and SMS. You can track your package in real-time using our order tracking page or the carrier's website. You'll also receive updates at each stage of delivery.",
        },
        {
          question: "What if my package is damaged or lost?",
          answer:
            "We take full responsibility for damaged or lost packages. If your order arrives damaged, please contact us within 48 hours with photos. For lost packages, we'll investigate with the carrier and either resend your order or provide a full refund.",
        },
      ],
    },
    {
      title: "Returns & Refunds",
      faqs: [
        {
          question: "What is your return policy?",
          answer:
            "We offer a 30-day satisfaction guarantee. If you're not completely happy with your purchase, you can return unopened products within 30 days for a full refund. Opened products can be returned if you experience an allergic reaction or skin irritation.",
        },
        {
          question: "How do I return a product?",
          answer:
            "To initiate a return, contact our customer service team or use our online return portal. We'll provide you with a prepaid return label and instructions. Once we receive and inspect your return, we'll process your refund within 3-5 business days.",
        },
        {
          question: "When will I receive my refund?",
          answer:
            "Refunds are processed within 3-5 business days after we receive your returned items. The refund will be credited to your original payment method. Bank transfers may take an additional 1-2 business days to reflect in your account.",
        },
        {
          question: "Can I exchange a product instead of returning it?",
          answer:
            "Yes! We offer free exchanges for different shades, sizes, or products of equal or lesser value. Simply indicate your preference when initiating the return process. Exchanges are processed faster than returns and refunds.",
        },
      ],
    },
    {
      title: "Account & Support",
      faqs: [
        {
          question: "How do I create an account?",
          answer:
            "Creating an account is easy! Click the 'Sign Up' button in the top right corner, enter your email and create a password. You can also sign up during checkout. Having an account allows you to track orders, save favorites, and access exclusive member benefits.",
        },
        {
          question: "I forgot my password. How do I reset it?",
          answer:
            "Click 'Forgot Password' on the login page and enter your email address. We'll send you a secure link to reset your password. If you don't receive the email within 10 minutes, check your spam folder or contact our support team.",
        },
        {
          question: "How can I contact customer support?",
          answer:
            "We're here to help! You can reach us via email at hello@fayozltd.com, call us at +234 (0) 123 456 7890, or use our live chat feature. Our support team is available Monday-Friday, 9AM-6PM WAT. We typically respond to emails within 24 hours.",
        },
        {
          question: "Do you have a loyalty program?",
          answer:
            "Yes! Our FayozPoints loyalty program rewards you for every purchase, review, and referral. Earn points that can be redeemed for discounts, free products, and exclusive perks. Members also get early access to new products and special sales.",
        },
      ],
    },
  ]

  // Transform API data to match component structure
  const faqCategories = faqData?.groupedFAQs
    ? Object.keys(faqData.groupedFAQs).map(category => ({
        title: category,
        faqs: faqData.groupedFAQs[category].map(faq => ({
          question: faq.question,
          answer: faq.answer,
        })),
      }))
    : defaultFaqCategories

  const allFaqs = faqCategories.flatMap((category, categoryIndex) =>
    category.faqs.map((faq, faqIndex) => ({
      ...faq,
      id: `${categoryIndex}-${faqIndex}`,
      category: category.title,
    })),
  )

  const filteredFaqs = allFaqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.category.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const toggleItem = (id) => {
    const newOpenItems = new Set(openItems)
    if (newOpenItems.has(id)) {
      newOpenItems.delete(id)
    } else {
      newOpenItems.add(id)
    }
    setOpenItems(newOpenItems)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <>
      <Seo {...routeSeoConfig.faq} type="website" />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-secondary/5"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-secondary/20 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <HelpCircle className="w-4 h-4" />
              Help Center
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 text-balance">
              Frequently Asked
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
                {" "}
                Questions
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto text-pretty">
              Find answers to common questions about our skincare products, shipping, returns, and more. Can't find what
              you're looking for? We're here to help!
            </p>

            {/* Search Bar */}
            <div className="max-w-md mx-auto relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search FAQs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 border-gray-200 focus:border-secondary focus:ring-secondary/20 bg-white/80 backdrop-blur-sm"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {searchTerm ? (
              // Search Results
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Search Results ({filteredFaqs.length})</h2>
                {filteredFaqs.length === 0 ? (
                  <Card className="border-0 shadow-lg">
                    <CardContent className="p-8 text-center">
                      <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">No results found</h3>
                      <p className="text-gray-600 mb-6">
                        We couldn't find any FAQs matching your search. Try different keywords or browse our categories
                        below.
                      </p>
                      <Button onClick={() => setSearchTerm("")} className="bg-primary hover:bg-primary/90 text-white">
                        Clear Search
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  filteredFaqs.map((faq, index) => (
                    <Card key={faq.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                      <CardContent className="p-0">
                        <button
                          onClick={() => toggleItem(faq.id)}
                          className="w-full p-6 text-left hover:bg-gray-50 transition-colors duration-200"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="text-sm text-primary font-medium mb-1">{faq.category}</div>
                              <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                            </div>
                            {openItems.has(faq.id) ? (
                              <ChevronUp className="w-5 h-5 text-primary flex-shrink-0" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-primary flex-shrink-0" />
                            )}
                          </div>
                        </button>
                        {openItems.has(faq.id) && (
                          <div className="px-6 pb-6">
                            <div className="border-t border-gray-100 pt-4">
                              <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            ) : (
              // Category View
              <div className="space-y-12">
                {faqCategories.map((category, categoryIndex) => (
                  <div key={categoryIndex}>
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">{category.title}</h2>
                    <div className="space-y-4">
                      {category.faqs.map((faq, faqIndex) => {
                        const itemId = `${categoryIndex}-${faqIndex}`
                        return (
                          <Card
                            key={itemId}
                            className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300"
                          >
                            <CardContent className="p-0">
                              <button
                                onClick={() => toggleItem(itemId)}
                                className="w-full p-6 text-left hover:bg-gray-50 transition-colors duration-200"
                              >
                                <div className="flex items-center justify-between">
                                  <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                                  {openItems.has(itemId) ? (
                                    <ChevronUp className="w-5 h-5 text-primary flex-shrink-0" />
                                  ) : (
                                    <ChevronDown className="w-5 h-5 text-primary flex-shrink-0" />
                                  )}
                                </div>
                              </button>
                              {openItems.has(itemId) && (
                                <div className="px-6 pb-6">
                                  <div className="border-t border-gray-100 pt-4">
                                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                                  </div>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact Support Section */}
      <section className="py-20 bg-gradient-to-br from-rose-50/50 to-pink-50/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Still Need Help?</h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Can't find the answer you're looking for? Our friendly customer support team is here to help you.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageCircle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Live Chat</h3>
                  <p className="text-gray-600 mb-4">Get instant help from our support team</p>
                  <Button className="bg-primary hover:bg-primary/90 text-white">Start Chat</Button>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Email Support</h3>
                  <p className="text-gray-600 mb-4">We'll respond within 24 hours</p>
                  <Link href="/contact">
                    <Button variant="outline" className="border-primary text-primary hover:bg-primary/5 bg-transparent">
                      Send Email
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                    <Phone className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Phone Support</h3>
                  <p className="text-gray-600 mb-4">Mon-Fri, 9AM-6PM WAT</p>
                  <Button variant="outline" className="border-primary text-primary hover:bg-primary/5 bg-transparent">
                    Call Now
                  </Button>
                </CardContent>
              </Card> */}
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}
